import * as React from 'react';
import { normalizeExternalUrl } from '../../lib/external-url.js';
import { logoUrl } from '../../lib/logo.js';

interface ContactAvatarInput {
  name: string;
  accountWebsite?: string | null;
  email?: string | null;
}

interface ContactAvatarProps extends ContactAvatarInput {
  size?: 'sm' | 'md';
}

export function contactInitials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

function emailDomain(email: string | null | undefined): string | null {
  const domain = email?.trim().split('@')[1]?.trim().toLowerCase();
  return domain || null;
}

export function getContactAvatarDisplay({ name, accountWebsite, email }: ContactAvatarInput) {
  const accountSource = accountWebsite?.trim() || null;
  const emailSource = emailDomain(email);
  // Ordered logo candidates: account domain first, then email domain. The
  // avatar cascades through these on load failure before showing initials.
  // De-duplicated so a shared account/email domain isn't tried twice.
  const logoSrcs = [
    ...new Set(
      [accountSource, emailSource].map((s) => logoUrl(s)).filter((s): s is string => Boolean(s)),
    ),
  ];

  const linkSource = accountSource || emailSource;
  const href = linkSource ? normalizeExternalUrl(linkSource) : undefined;

  return {
    href,
    hrefLabel: `Open ${name}`,
    initials: contactInitials(name),
    logoSrcs,
  };
}

export function ContactAvatar({ name, accountWebsite, email, size = 'md' }: ContactAvatarProps) {
  const display = getContactAvatarDisplay({ name, accountWebsite, email });
  // Track logo URLs that failed to load; the active source is the first
  // candidate still standing (cascades account -> email -> initials). The drawer
  // stays mounted across contacts (prev/next, related-record navigation), so reset the
  // failed set during render when the candidate logos change — a new contact
  // must retry its own logos rather than inherit the previous one's failures.
  const candidateKey = display.logoSrcs.join('|');
  const [errored, setErrored] = React.useState({ key: candidateKey, srcs: new Set<string>() });
  if (errored.key !== candidateKey) {
    setErrored({ key: candidateKey, srcs: new Set() });
  }
  const erroredSrcs = errored.key === candidateKey ? errored.srcs : new Set<string>();
  const activeSrc = display.logoSrcs.find((src) => !erroredSrcs.has(src)) ?? null;
  const showImage = Boolean(activeSrc);
  const sizeClass = size === 'sm' ? 'h-8 w-8 text-xs' : 'h-12 w-12 text-sm';
  const className = [
    'flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-muted font-medium text-muted-foreground',
    display.href
      ? 'cursor-pointer transition hover:scale-105 hover:bg-muted/80 focus-visible:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
      : '',
    sizeClass,
  ]
    .filter(Boolean)
    .join(' ');
  const content =
    showImage && activeSrc ? (
      <img
        src={activeSrc}
        alt={name}
        className="h-full w-full object-contain"
        onError={() =>
          setErrored((prev) => ({ key: candidateKey, srcs: new Set(prev.srcs).add(activeSrc) }))
        }
      />
    ) : (
      display.initials
    );

  if (display.href) {
    return (
      <a
        href={display.href}
        target="_blank"
        rel="noreferrer"
        aria-label={display.hrefLabel}
        className={className}
        onClick={(event) => event.stopPropagation()}
      >
        {content}
      </a>
    );
  }

  return <div className={className}>{content}</div>;
}
